import { Injectable } from '@angular/core';
import type { LogsInitConfiguration } from '@datadog/browser-logs/src/domain/configuration';
import { datadogLogs } from '@datadog/browser-logs';
import { environment } from '../environments/environment';
import * as appVersion from '../app-version.json';
const versionFile = appVersion;
// eslint-disable-next-line @nx/enforce-module-boundaries
import { DatadogConfiguration } from '@jump-tech-frontend/relay/shared';

/**
 * object keys are environment names, value is array of tenants that should not be tracked in that environment
 */
const DO_NOT_TRACK_TENANTS = {
  preview: ['regression', 'test'],
  dev: ['regression', 'test'],
  staging: ['regression', 'test'],
  production: ['regression', 'test']
};

export const PII_KEYS = [
  'firstName',
  'lastName',
  'printName',
  'contractorName',
  'contractorName',
  'preferredCPMname',
  'quoteAcceptedName',
  'siteContactName',
  'siteContactNameChargingStations',
  'billingCompanyName',
  'emailChargingStations',
  'emailLoadManagement',
  'email',
  'inputTextEmail',
  'CompanyEmail',
  'phoneNumber'
];

type Environment = typeof environment & { datadog?: DatadogConfiguration };

const RedactPII = obj => {
  // remove any keys that contain PII
  Object.keys(obj).forEach((key: string): void => {
    if (PII_KEYS.includes(key)) {
      obj[key] = 'REDACTED';
    }
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      RedactPII(obj[key]);
    }
  });
};

@Injectable({
  providedIn: 'root'
})
export class DatadogLoggerService {
  private loggerInitialized = false;

  private datadogLoggerConfig: LogsInitConfiguration = {
    site: environment.datadog?.site,
    env: environment.name,
    service: environment?.datadog?.service,
    sessionSampleRate: environment?.datadog?.sampleRate,
    version: versionFile.appVersion,
    useSecureSessionCookie: true,
    forwardConsoleLogs: environment?.datadog?.forwardConsoleLogs,
    forwardErrorsToLogs: true,
    clientToken: environment.datadog?.clientTokenLogging,
    trackingConsent: environment.datadog?.trackingConsent,
    sessionPersistence: 'local-storage',
    beforeSend: this.sanitizeLogData
  };

  constructor() {
    const tenant = window.location.hostname.substring(0, window.location.hostname.indexOf('.'));
    if (!this.allowLoggingForTenant(tenant)) {
      return;
    }
    this.init(tenant).then();
  }

  private initDatadogLogs(tenant: string, hashedSuid: string): void {
    if (!this.loggerInitialized && this.canInitLogsForTenant(tenant)) {
      datadogLogs.init(this.datadogLoggerConfig);
      datadogLogs.setGlobalContextProperty('context.tenant', tenant);
      datadogLogs.setGlobalContextProperty('context.userId', hashedSuid);
      this.loggerInitialized = true;
    }
  }

  private sanitizeLogData(log): boolean {
    // scrub sensitive data from error context
    if (log.errorContext && log.errorContext.form) {
      RedactPII(log.errorContext.form);
    }

    // don't send missing i18n errors
    if (log.message.includes('i18n: Missing translation for key')) {
      return false;
    }
    return true;
  }

  private canInitLogsForTenant(tenant: string): boolean {
    return this.allowLoggingForTenant(tenant) && this.isDatadogConfigured(environment);
  }

  private async init(tenant: string): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const uuid = params.get('suid');
    const hashedSuid = await this.digestMessage(uuid);
    this.initDatadogLogs(tenant, hashedSuid);
  }

  private isDatadogConfigured(env: Environment): env is Environment & { datadog: DatadogConfiguration } {
    return Object.prototype.hasOwnProperty.call(env, 'datadog') && environment?.datadog.applicationId !== undefined;
  }

  private allowLoggingForTenant(tenant: string): boolean {
    if (tenant === undefined) {
      return false;
    }
    if (Object.prototype.hasOwnProperty.call(DO_NOT_TRACK_TENANTS, environment.name)) {
      return !DO_NOT_TRACK_TENANTS[environment.name].includes(tenant);
    }
    return true;
  }

  private async digestMessage(message: string): Promise<string> {
    // takes a content string and returns a hashed HEX
    const msgUint8 = new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8); // hash the message
    const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
    // convert bytes to hex string
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }
}
